import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import CertificateTemplate from '../components/CertificateTemplate';
import api from '../services/api';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const API_URL   = process.env.REACT_APP_API_URL   || 'http://localhost:5002/api/v1';
const BASE_URL  = window.location.origin;
const LMS_URL   = process.env.REACT_APP_LMS_URL   || 'http://localhost:3002';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}
const getLMSUrl = () => {
  const token = localStorage.getItem('spa_token');
  return token ? `${LMS_URL}/auth?token=${encodeURIComponent(token)}` : `${LMS_URL}/login`;
};

const getLMSCourseUrl = (courseId) => {
  const token = localStorage.getItem('spa_token');
  return token
    ? `${LMS_URL}/auth?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(`/course/${courseId}`)}`
    : `${LMS_URL}/login`;
};

const STATUS_COLORS = {
  pending:'#f0b832', approved:'#3ecf8e', enrolled:'#4e9cf5',
  completed:'#e8a43a', rejected:'#f06060', withdrawn:'#888',
  applied:'#4e9cf5', reviewing:'#f0b832', accepted:'#3ecf8e', active:'#e8a43a',
};

function ProgressBar({ pct, color='var(--gold)' }) {
  return (
    <div style={{ height:4, background:'var(--bg-2)', borderRadius:2, overflow:'hidden', marginTop:6 }}>
      <div style={{ height:'100%', width:`${pct||0}%`, background:color, borderRadius:2, transition:'width 0.5s' }}/>
    </div>
  );
}

// ── Image Upload Helper ───────────────────────────────────
async function uploadImage(file, token) {
  const fd = new FormData();
  fd.append('files', file);
  const res = await fetch(`${MEDIA_URL}/api/media/upload/batch`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const data = await res.json();
  return data.data?.[0]?.file_url || null;
}

// ── Avatar Component ──────────────────────────────────────
function Avatar({ user, size=52, editable=false, onUploaded }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const color = { student:'var(--gold)', trainer:'#4e9cf5', partner:'#3ecf8e' }[user?.role] || 'var(--gold)';
  const src   = resolveMedia(user?.avatar_url);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('spa_token');
      const url   = await uploadImage(file, token);
      if (url) {
        await api.put('/auth/profile', { avatar_url: url });
        onUploaded?.(url);
      }
    } catch { alert('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ position:'relative', flexShrink:0 }}>
      <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden', background:`${color}18`, border:`2px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:editable?'pointer':'default' }}
        onClick={()=>editable&&fileRef.current?.click()}>
        {uploading
          ? <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.2)', borderTopColor:color, borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
          : src
            ? <img src={src} alt={user?.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <span style={{ fontFamily:'var(--font-display)', fontWeight:700, color, fontSize:size*0.38 }}>{user?.name?.[0]?.toUpperCase()||'U'}</span>
        }
      </div>
      {editable && (
        <>
          <div style={{ position:'absolute', bottom:-2, right:-2, width:20, height:20, borderRadius:'50%', background:'var(--gold)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, cursor:'pointer', border:'2px solid var(--bg-1)' }}
            onClick={()=>fileRef.current?.click()}>📷</div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleFile}/>
        </>
      )}
    </div>
  );
}

// ── Profile Modal ─────────────────────────────────────────
function ProfileModal({ user, onClose, onSaved }) {
  const [mode,    setMode]    = useState('view');
  const [form,    setForm]    = useState({ name:user?.name||'', phone:user?.phone||'', bio:user?.bio||'' });
  const [pwForm,  setPwForm]  = useState({ current_password:'', new_password:'', confirm:'' });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');
  const [err,     setErr]     = useState('');
  const [userData,setUD]      = useState(user);
  const coverRef = useRef();
  const [coverUp, setCoverUp] = useState(false);

  const roleColor = { student:'var(--gold)', trainer:'#4e9cf5', partner:'#3ecf8e' }[user?.role] || 'var(--gold)';

  const saveProfile = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      await api.put('/auth/profile', { name:form.name, phone:form.phone, bio:form.bio });
      setMsg('Profile updated'); onSaved?.(); setMode('view');
    } catch(e) { setErr(e.response?.data?.error||'Update failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { setErr('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6)         { setErr('Min 6 characters'); return; }
    setSaving(true); setMsg(''); setErr('');
    try {
      await userAPI.changePassword({ current_password:pwForm.current_password, new_password:pwForm.new_password });
      setMsg('Password changed'); setPwForm({ current_password:'', new_password:'', confirm:'' }); setMode('view');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUp(true);
    try {
      const token = localStorage.getItem('spa_token');
      const url   = await uploadImage(file, token);
      if (url) {
        await api.put('/auth/profile', { cover_url: url });
        setUD(u => ({ ...u, cover_url: url }));
        onSaved?.();
      }
    } catch { alert('Cover upload failed'); }
    finally { setCoverUp(false); }
  };

  const inp = { width:'100%', background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--t-primary)', padding:'10px 14px', fontSize:'0.88rem', outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
  const lbl = { fontSize:'0.7rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', display:'block', marginBottom:5, marginTop:14 };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:20, width:'100%', maxWidth:460, overflow:'hidden' }}>

        {/* Cover photo */}
        <div style={{ height:120, background:'var(--bg-2)', position:'relative', overflow:'hidden', cursor:'pointer' }}
          onClick={()=>coverRef.current?.click()}>
          {userData?.cover_url
            ? <img src={resolveMedia(userData.cover_url)} alt="Cover" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', background:`linear-gradient(135deg, var(--bg-3), ${roleColor}22)` }}/>
          }
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.3)', opacity:0, transition:'opacity 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.opacity='1'} onMouseLeave={e=>e.currentTarget.style.opacity='0'}>
            <span style={{ color:'#fff', fontSize:'0.8rem', fontWeight:600 }}>{coverUp?'Uploading…':'📷 Change Cover'}</span>
          </div>
          <input ref={coverRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleCover}/>
        </div>

        {/* Avatar overlapping cover */}
        <div style={{ padding:'0 24px', marginTop:-32, marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
          <Avatar user={userData} size={64} editable onUploaded={url=>{setUD(u=>({...u,avatar_url:url}));onSaved?.();}}/>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--t-muted)', fontSize:'1.2rem', cursor:'pointer', padding:4, marginBottom:4 }}>✕</button>
        </div>

        <div style={{ padding:'4px 24px 24px' }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:'0.95rem' }}>{userData?.name}</div>
            <div style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>{userData?.email}</div>
          </div>

          {msg && <div style={{ padding:'10px 14px', background:'rgba(62,207,142,0.1)', border:'1px solid rgba(62,207,142,0.3)', borderRadius:8, fontSize:'0.84rem', color:'#3ecf8e', marginBottom:14 }}>✓ {msg}</div>}
          {err && <div style={{ padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:'0.84rem', color:'#ef4444', marginBottom:14 }}>⚠ {err}</div>}

          {mode==='view' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[
                ['Full Name', userData?.name],
                ['Email',     userData?.email],
                ['Phone',     userData?.phone||'—'],
                ['Role',      userData?.role?.replace(/_/g,' ')],
                ['Bio',       userData?.bio||'—'],
              ].map(([k,v])=>(
                <div key={k} style={{ display:'flex', flexDirection:'column', gap:2 }}>
                  <div style={{ fontSize:'0.68rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600 }}>{k}</div>
                  <div style={{ fontSize:'0.88rem', color:'var(--t-primary)', lineHeight:1.5, textTransform:k==='Role'?'capitalize':'none' }}>{v}</div>
                </div>
              ))}
              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button onClick={()=>{setMode('edit');setMsg('');setErr('');}}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.85rem', fontFamily:'inherit', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=roleColor;e.currentTarget.style.color=roleColor;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
                  ✎ Edit Profile
                </button>
                <button onClick={()=>{setMode('password');setMsg('');setErr('');}}
                  style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.85rem', fontFamily:'inherit', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-2)';e.currentTarget.style.color='var(--t-primary)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-secondary)';}}>
                  🔒 Password
                </button>
              </div>
            </div>
          )}

          {mode==='edit' && (
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={lbl}>Full Name</label>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={inp}/>
              <label style={lbl}>Phone</label>
              <input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="0781234567" style={inp}/>
              <label style={lbl}>Bio (optional)</label>
              <textarea value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} rows={3} style={{ ...inp, resize:'none', lineHeight:1.6 }} placeholder="A short bio…"/>
              <div style={{ display:'flex', gap:10, marginTop:14 }}>
                <button onClick={()=>setMode('view')} style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.85rem', fontFamily:'inherit' }}>← Back</button>
                <button onClick={saveProfile} disabled={saving} style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:roleColor, color:'#080a0d', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:'0.85rem' }}>
                  {saving?'Saving…':'Save Profile'}
                </button>
              </div>
            </div>
          )}

          {mode==='password' && (
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={lbl}>Current Password</label>
              <input type="password" value={pwForm.current_password} onChange={e=>setPwForm(f=>({...f,current_password:e.target.value}))} style={inp}/>
              <label style={lbl}>New Password</label>
              <input type="password" value={pwForm.new_password} onChange={e=>setPwForm(f=>({...f,new_password:e.target.value}))} style={inp}/>
              <label style={lbl}>Confirm New Password</label>
              <input type="password" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))} style={inp}/>
              <div style={{ display:'flex', gap:10, marginTop:14 }}>
                <button onClick={()=>setMode('view')} style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.85rem', fontFamily:'inherit' }}>← Back</button>
                <button onClick={changePassword} disabled={saving} style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:roleColor, color:'#080a0d', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:'0.85rem' }}>
                  {saving?'Saving…':'Change Password'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Certificate Card ──────────────────────────────────────
function CertificateCard({ cert }) {
  const [showQR, setShowQR]     = useState(false);
  const [showCert, setShowCert] = useState(false);
  const certUrl = `${BASE_URL}/verify/${cert.cert_hash}`;
  return (
    <>
      <div style={{ background:'var(--bg-1)', border:'2px solid rgba(62,207,142,0.3)', borderRadius:'var(--r-xl)', overflow:'hidden' }}>
        <div style={{ padding:'24px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'rgba(62,207,142,0.12)', border:'1px solid rgba(62,207,142,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color:'#3ecf8e' }}>◈</div>
            <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 8px', borderRadius:20, background:'rgba(62,207,142,0.12)', color:'#3ecf8e', border:'1px solid rgba(62,207,142,0.3)' }}>Certified</span>
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:'0.7rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Certificate of Completion</div>
            <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, lineHeight:1.3, marginBottom:4 }}>{cert.program_title}</h3>
            {cert.cohort_name && <div style={{ fontSize:'0.78rem', color:'var(--t-muted)' }}>Cohort: {cert.cohort_name}</div>}
          </div>
          <div style={{ fontSize:'0.78rem', color:'var(--t-muted)', marginBottom:16 }}>
            Issued {new Date(cert.issued_at).toLocaleDateString('en-RW',{year:'numeric',month:'long',day:'numeric'})}
          </div>
          {showQR && (
            <div style={{ background:'var(--bg-2)', borderRadius:10, padding:'14px', marginBottom:14, display:'flex', gap:14, alignItems:'center' }}>
              <div style={{ background:'#fff', padding:6, borderRadius:8, flexShrink:0 }}>
                <QRCodeSVG value={certUrl} size={72} level="H" includeMargin={false}/>
              </div>
              <div>
                <div style={{ fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>Scan to verify</div>
                <button onClick={()=>{navigator.clipboard.writeText(certUrl);alert('Link copied!');}}
                  style={{ fontSize:'0.72rem', color:'var(--gold)', background:'none', border:'none', cursor:'pointer', padding:0 }}>Copy link →</button>
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setShowCert(true)} className="btn btn-primary" style={{ flex:1, justifyContent:'center', fontSize:'0.82rem', background:'rgba(62,207,142,0.15)', color:'#3ecf8e', border:'1px solid rgba(62,207,142,0.4)' }}>⬇ Download</button>
            <button onClick={()=>setShowQR(v=>!v)} style={{ padding:'8px 14px', borderRadius:'var(--r-sm)', background:showQR?'rgba(232,164,58,0.1)':'var(--bg-2)', border:`1px solid ${showQR?'rgba(232,164,58,0.35)':'var(--border)'}`, color:showQR?'var(--gold)':'var(--t-muted)', fontSize:'0.82rem', cursor:'pointer' }}>⬡ QR</button>
          </div>
        </div>
      </div>
      {showCert && <CertificateTemplate cert={cert} onClose={()=>setShowCert(false)}/>}
    </>
  );
}

// ── Enrollment Card ───────────────────────────────────────
function EnrollmentCard({ e, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isActive = !['rejected','withdrawn'].includes(e.status);
  const color = STATUS_COLORS[e.status] || '#666';
  return (
    <div style={{ background:'var(--bg-1)', border:`1px solid ${open?'var(--border)':'transparent'}`, borderRadius:'var(--r-xl)', overflow:'hidden', opacity:isActive?1:0.65, transition:'all 0.2s' }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ display:'flex', alignItems:'center', gap:12, padding:open?'16px 20px 12px':'14px 20px', cursor:'pointer', background:open?'transparent':'var(--bg-2)', transition:'background 0.15s' }}>
        {e.media_url && <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', flexShrink:0 }}><img src={resolveMedia(e.media_url)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/></div>}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:'0.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.program_title||'Program'}</div>
          {e.cohort_name && <div style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>{e.cohort_name}</div>}
        </div>
        <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${color}18`, color, border:`1px solid ${color}33`, whiteSpace:'nowrap', textTransform:'capitalize', flexShrink:0 }}>{e.status}</span>
        {isActive && e.progress_pct>0 && <span style={{ fontSize:'0.72rem', color:'var(--t-muted)', flexShrink:0 }}>{e.progress_pct}%</span>}
        <span style={{ color:'var(--t-muted)', fontSize:'0.8rem', flexShrink:0, transition:'transform 0.2s', transform:open?'rotate(180deg)':'none' }}>▾</span>
      </div>
      {open && (
        <div style={{ padding:'0 20px 16px' }}>
          {e.progress && e.progress!=='not_started' && (
            <div style={{ marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:'var(--t-muted)', marginBottom:2 }}>
                <span style={{ textTransform:'capitalize' }}>{e.progress.replace('_',' ')}</span>
                <span>{e.progress_pct||0}%</span>
              </div>
              <ProgressBar pct={e.progress_pct} color={STATUS_COLORS[e.progress]||'var(--gold)'}/>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>
              Applied {new Date(e.created_at).toLocaleDateString('en-RW',{month:'short',day:'numeric',year:'numeric'})}
              {e.payment_status && <span style={{ marginLeft:10, color:e.payment_status==='paid'?'#3ecf8e':'var(--t-muted)' }}>· Payment: {e.payment_status}</span>}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              {e.certificate_hash && <Link to={`/verify/${e.certificate_hash}`} style={{ fontSize:'0.78rem', color:'#3ecf8e', fontWeight:600 }}>◈ View Certificate →</Link>}
              {isActive && e.course_id && <a href={getLMSCourseUrl(e.course_id)} target="_blank" rel="noopener noreferrer" style={{ fontSize:'0.78rem', color:'var(--gold)', fontWeight:600 }}>🎓 Open in LMS →</a>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Student Dashboard ─────────────────────────────────────
function StudentDashboard() {
  const [tab, setTab]            = useState('enrollments');
  const [enrollments, setEnr]    = useState([]);
  const [applications, setApps]  = useState([]);
  const [certificates, setCerts] = useState([]);
  const [loading, setLoading]    = useState(true);
  const [statusFilter, setFilt]  = useState('active');
  const [collapsed, setCollapsed]= useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      userAPI.myEnrollments().then(r  => setEnr(r.data?.data   || [])),
      userAPI.myInternships().then(r  => setApps(r.data?.data  || [])),
      userAPI.myCertificates().then(r => setCerts(r.data?.data || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const filtered = enrollments.filter(e => {
    if (statusFilter==='active')    return !['rejected','withdrawn','completed'].includes(e.status);
    if (statusFilter==='completed') return e.status==='completed';
    if (statusFilter==='rejected')  return ['rejected','withdrawn'].includes(e.status);
    return true;
  });

  const tabStyle = (t) => ({ padding:'10px 20px', fontSize:'0.875rem', cursor:'pointer', background:'none', border:'none', borderBottom:`2px solid ${tab===t?'var(--gold)':'transparent'}`, color:tab===t?'var(--gold)':'var(--t-muted)', fontWeight:tab===t?700:400, transition:'all 0.15s', marginBottom:-1 });
  const filterBtn = (v, label) => (
    <button onClick={()=>setFilt(v)} style={{ padding:'5px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontFamily:'inherit', border:`1px solid ${statusFilter===v?'var(--gold)':'var(--border)'}`, background:statusFilter===v?'rgba(232,164,58,0.12)':'transparent', color:statusFilter===v?'var(--gold)':'var(--t-muted)', fontWeight:statusFilter===v?600:400, transition:'all 0.15s' }}>
      {label}
    </button>
  );

  return (
    <>
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ display:'flex' }}>
          <button style={tabStyle('enrollments')} onClick={()=>setTab('enrollments')}>My Programs</button>
          <button style={tabStyle('applications')} onClick={()=>setTab('applications')}>My Internships</button>
          <button style={tabStyle('certificates')} onClick={()=>setTab('certificates')}>
            My Certificates
            {certificates.length>0 && <span style={{ marginLeft:6, fontSize:'0.65rem', padding:'2px 6px', borderRadius:10, background:'rgba(62,207,142,0.15)', color:'#3ecf8e', fontWeight:700 }}>{certificates.length}</span>}
          </button>
        </div>
      </div>
      <section style={{ padding:'clamp(32px,5vw,56px) 0', minHeight:'50vh' }}>
        <div className="container">
          {loading ? <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>Loading…</div>
          : tab==='enrollments' ? (
            enrollments.length===0
              ? <div style={{ textAlign:'center', padding:60 }}><div style={{ fontSize:'2.5rem', marginBottom:16, opacity:0.2 }}>◐</div><p style={{ color:'var(--t-muted)', marginBottom:20 }}>No programs yet.</p><Link to="/academy" className="btn btn-primary" style={{ margin:'0 auto' }}>Browse Programs →</Link></div>
              : <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {filterBtn('active',    `Active (${enrollments.filter(e=>!['rejected','withdrawn','completed'].includes(e.status)).length})`)}
                      {filterBtn('completed', `Completed (${enrollments.filter(e=>e.status==='completed').length})`)}
                      {filterBtn('rejected',  `Rejected (${enrollments.filter(e=>['rejected','withdrawn'].includes(e.status)).length})`)}
                      {filterBtn('all',       `All (${enrollments.length})`)}
                    </div>
                    <button onClick={()=>setCollapsed(c=>!c)} style={{ padding:'5px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontFamily:'inherit', border:'1px solid var(--border)', background:'transparent', color:'var(--t-muted)' }}>
                      {collapsed?'⊞ Expand All':'⊟ Collapse All'}
                    </button>
                  </div>
                  {filtered.length===0
                    ? <div style={{ textAlign:'center', padding:40, color:'var(--t-muted)', fontSize:'0.88rem' }}>No programs in this category.</div>
                    : filtered.map(e=><EnrollmentCard key={e.id} e={e} defaultOpen={!collapsed&&!['rejected','withdrawn'].includes(e.status)}/>)
                  }
                </div>
          ) : tab==='applications' ? (
            applications.length===0
              ? <div style={{ textAlign:'center', padding:60 }}><div style={{ fontSize:'2.5rem', marginBottom:16, opacity:0.2 }}>◉</div><p style={{ color:'var(--t-muted)', marginBottom:20 }}>No internship applications yet.</p><Link to="/academy?tab=internships" className="btn btn-primary" style={{ margin:'0 auto' }}>Browse Internships →</Link></div>
              : <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {applications.map(a=>(
                    <div key={a.id} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                      <div>
                        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'0.95rem', fontWeight:600, marginBottom:4 }}>{a.internship_title||'Internship'}</h3>
                        <div style={{ fontSize:'0.78rem', color:'var(--t-muted)' }}>Applied {new Date(a.created_at).toLocaleDateString('en-RW',{month:'short',day:'numeric',year:'numeric'})}</div>
                      </div>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${STATUS_COLORS[a.status]||'#666'}18`, color:STATUS_COLORS[a.status]||'#666', border:`1px solid ${STATUS_COLORS[a.status]||'#666'}33`, textTransform:'capitalize' }}>{a.status}</span>
                    </div>
                  ))}
                </div>
          ) : (
            certificates.length===0
              ? <div style={{ textAlign:'center', padding:60 }}><div style={{ fontSize:'2.5rem', marginBottom:16, opacity:0.2 }}>◈</div><p style={{ color:'var(--t-muted)', marginBottom:20 }}>No certificates yet.</p><Link to="/academy" className="btn btn-primary" style={{ margin:'0 auto' }}>Browse Programs →</Link></div>
              : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>{certificates.map(c=><CertificateCard key={c.id} cert={c}/>)}</div>
          )}
        </div>
      </section>
    </>
  );
}

// ── Trainer + Partner dashboards unchanged ────────────────
function TrainerDashboard() {
  const [tab,setCohTab]         = useState('cohorts');
  const [cohorts,setCohorts]    = useState([]);
  const [students,setStudents]  = useState([]);
  const [selCohort,setSel]      = useState(null);
  const [loading,setLoading]    = useState(true);
  const [assessModal,setAM]     = useState(null);
  const [assessForm,setAF]      = useState({});
  const [saving,setSaving]      = useState(false);

  useEffect(()=>{ api.get('/my/cohorts').then(r=>setCohorts(r.data?.data||[])).catch(()=>{}).finally(()=>setLoading(false)); },[]);

  const loadStudents = async (cohortId) => {
    setLoading(true); setSel(cohortId);
    try { const r=await api.get(`/my/students?cohort_id=${cohortId}`); setStudents(r.data?.data||[]); setCohTab('students'); }
    catch { setStudents([]); } finally { setLoading(false); }
  };

  const openAssess = (s) => { setAF({ score:s.assessment_score||'', grade:s.assessment_grade||'', attendance_pct:s.attendance_pct||'', notes:s.trainer_notes||'', recommend_cert:s.recommend_cert||false, enrollment_id:s.id, cohort_id:s.cohort_id }); setAM(s); };
  const handleAssess = async () => { setSaving(true); try { await api.post('/my/assessments',assessForm); setAM(null); loadStudents(selCohort); } catch { alert('Failed'); } finally { setSaving(false); }};

  const tabStyle = (t) => ({ padding:'10px 20px', fontSize:'0.875rem', cursor:'pointer', background:'none', border:'none', borderBottom:`2px solid ${tab===t?'var(--gold)':'transparent'}`, color:tab===t?'var(--gold)':'var(--t-muted)', fontWeight:tab===t?700:400, transition:'all 0.15s', marginBottom:-1 });

  return (
    <>
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ display:'flex' }}>
          <button style={tabStyle('cohorts')} onClick={()=>setCohTab('cohorts')}>My Cohorts</button>
          {selCohort && <button style={tabStyle('students')} onClick={()=>setCohTab('students')}>Students</button>}
        </div>
      </div>
      <section style={{ padding:'clamp(32px,5vw,56px) 0', minHeight:'50vh' }}>
        <div className="container">
          {loading ? <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>Loading…</div>
          : tab==='cohorts'
            ? cohorts.length===0
              ? <div style={{ textAlign:'center', padding:60 }}><div style={{ fontSize:'2.5rem', marginBottom:16, opacity:0.2 }}>◐</div><p style={{ color:'var(--t-muted)' }}>No cohorts assigned yet.</p></div>
              : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:16 }}>
                  {cohorts.map(c=>(
                    <div key={c.id} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:'24px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                        <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 8px', borderRadius:20, background:'rgba(232,164,58,0.12)', color:'var(--gold)', border:'1px solid rgba(232,164,58,0.3)', textTransform:'capitalize' }}>{c.trainer_role}</span>
                        <span style={{ fontSize:'0.7rem', color:'var(--t-muted)' }}>{c.enrolled_count||0} students</span>
                      </div>
                      <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, marginBottom:6 }}>{c.program_title}</h3>
                      <div style={{ fontSize:'0.82rem', color:'var(--t-muted)', marginBottom:16 }}>{c.cohort_name}</div>
                      <button onClick={()=>loadStudents(c.id)} className="btn btn-outline" style={{ width:'100%', justifyContent:'center', fontSize:'0.85rem' }}>View Students →</button>
                    </div>
                  ))}
                </div>
            : students.length===0
              ? <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>No students yet.</div>
              : <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {students.map(s=>(
                    <div key={s.id} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:'18px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, marginBottom:2 }}>{s.full_name}</div>
                        <div style={{ fontSize:'0.78rem', color:'var(--t-muted)' }}>{s.email}</div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${STATUS_COLORS[s.status]||'#666'}18`, color:STATUS_COLORS[s.status]||'#666', border:`1px solid ${STATUS_COLORS[s.status]||'#666'}33`, textTransform:'capitalize' }}>{s.status}</span>
                        <button onClick={()=>openAssess(s)} className="btn btn-outline" style={{ fontSize:'0.82rem' }}>Assess</button>
                      </div>
                    </div>
                  ))}
                </div>
          }
        </div>
      </section>
      {assessModal && (
        <div onClick={()=>setAM(null)} style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(0,0,0,0.82)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', width:'100%', maxWidth:480, padding:'28px 32px' }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:600, marginBottom:20 }}>Assess — {assessModal.full_name}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
              <div className="field"><label>Score (0–100)</label><input type="number" min="0" max="100" value={assessForm.score} onChange={e=>setAF(f=>({...f,score:e.target.value}))}/></div>
              <div className="field"><label>Grade</label><input value={assessForm.grade} onChange={e=>setAF(f=>({...f,grade:e.target.value}))} placeholder="A, B, Pass…"/></div>
              <div className="field" style={{ gridColumn:'1/-1' }}><label>Attendance %</label><input type="number" min="0" max="100" value={assessForm.attendance_pct} onChange={e=>setAF(f=>({...f,attendance_pct:e.target.value}))}/></div>
              <div className="field" style={{ gridColumn:'1/-1' }}><label>Notes</label><textarea value={assessForm.notes} onChange={e=>setAF(f=>({...f,notes:e.target.value}))} rows={3}/></div>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.875rem', marginBottom:20, cursor:'pointer' }}>
              <input type="checkbox" checked={assessForm.recommend_cert} onChange={e=>setAF(f=>({...f,recommend_cert:e.target.checked}))}/>
              <span style={{ color:'#3ecf8e' }}>Recommend for certificate</span>
            </label>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={()=>setAM(null)} className="btn btn-outline">Cancel</button>
              <button onClick={handleAssess} disabled={saving} className="btn btn-primary">{saving?'Saving…':'Save Assessment'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PartnerDashboard() {
  const [institution,setInst] = useState(null);
  const [students,setStudents]= useState([]);
  const [loading,setLoading]  = useState(true);
  useEffect(()=>{ Promise.all([api.get('/my/institution').then(r=>setInst(r.data?.data)),api.get('/my/partner-students').then(r=>setStudents(r.data?.data||[]))]).finally(()=>setLoading(false)); },[]);
  if (loading) return <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>Loading…</div>;
  if (!institution) return <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>No institution linked.</div>;
  const completed=students.filter(s=>['completed'].includes(s.enrollment_status||s.status)).length;
  return (
    <section style={{ padding:'clamp(32px,5vw,56px) 0' }}>
      <div className="container">
        <div style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:'28px 32px', marginBottom:28 }}>
          <div style={{ display:'flex', gap:20, alignItems:'flex-start', flexWrap:'wrap' }}>
            {institution.logo_url && <img src={resolveMedia(institution.logo_url)} alt="" style={{ width:60, height:60, borderRadius:12, objectFit:'contain', background:'var(--bg-2)', padding:6 }}/>}
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:4 }}>{institution.name}</div>
              <div style={{ fontSize:'0.85rem', color:'var(--t-muted)', marginBottom:10 }}>{institution.type} · {institution.partnership_type||'Partner'}</div>
              <div style={{ display:'flex', gap:24 }}>
                <div><div style={{ fontSize:'0.65rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Total Students</div><div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, color:'var(--gold)' }}>{institution.student_count||0}</div></div>
                <div><div style={{ fontSize:'0.65rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Completed</div><div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, color:'#3ecf8e' }}>{completed}</div></div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'1rem', marginBottom:16 }}>Your Students</div>
        {students.length===0
          ? <div style={{ textAlign:'center', padding:60, color:'var(--t-muted)' }}>No students yet.</div>
          : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {students.map(s=>(
                <div key={s.id} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'16px 22px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                  <div><div style={{ fontWeight:600, marginBottom:2 }}>{s.student_name}</div><div style={{ fontSize:'0.78rem', color:'var(--t-muted)' }}>{s.student_email}</div></div>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${STATUS_COLORS[s.enrollment_status||s.status]||'#666'}18`, color:STATUS_COLORS[s.enrollment_status||s.status]||'#666', border:`1px solid ${STATUS_COLORS[s.enrollment_status||s.status]||'#666'}33`, textTransform:'capitalize' }}>{s.enrollment_status||s.status}</span>
                </div>
              ))}
            </div>
        }
      </div>
    </section>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────
export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [userData, setUserData]       = useState(null);
  const role = user?.role;

  const refreshUser = () => api.get('/auth/me').then(r=>setUserData(r.data?.data||r.data?.user||user)).catch(()=>{});

  useEffect(() => { if(user) setUserData(user); refreshUser(); }, [user]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const roleColors = { student:'var(--gold)', trainer:'#4e9cf5', partner:'#3ecf8e' };
  const roleLabels = { student:'Student', trainer:'Trainer', partner:'Partner Institution' };
  const roleColor  = roleColors[role] || 'var(--gold)';

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-0)', paddingTop:'var(--nav-h)' }}>

      {/* Header with cover photo */}
      <div style={{ position:'relative' }}>
        {/* Cover */}
        <div style={{ height:180, background:'var(--bg-1)', overflow:'hidden' }}>
          {userData?.cover_url
            ? <img src={resolveMedia(userData.cover_url)} alt="Cover" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            : <div style={{ width:'100%', height:'100%', background:`linear-gradient(135deg, var(--bg-1) 0%, ${roleColor}18 100%)` }}/>
          }
        </div>

        {/* Profile card overlapping cover */}
        <section style={{ background:'var(--bg-1)', borderBottom:'1px solid var(--border)' }}>
          <div className="container">
            <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:20, flexWrap:'wrap', paddingBottom:24, marginTop:-36 }}>
              <div style={{ display:'flex', alignItems:'flex-end', gap:16 }}>
                <Avatar user={userData} size={72} editable onUploaded={()=>refreshUser()}/>
                <div style={{ paddingBottom:4 }}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700 }}>{userData?.name}</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:3 }}>
                    <span style={{ fontSize:'0.75rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:`${roleColor}15`, color:roleColor, border:`1px solid ${roleColor}33` }}>{roleLabels[role]||role}</span>
                    <span style={{ fontSize:'0.78rem', color:'var(--t-muted)' }}>{userData?.email}</span>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, paddingBottom:4 }}>
                {role==='student' && <Link to="/academy" className="btn btn-outline" style={{ fontSize:'0.85rem' }}>Browse Programs</Link>}
                {role==='trainer' && <Link to="/academy" className="btn btn-outline" style={{ fontSize:'0.85rem' }}>View Programs</Link>}
                <button onClick={()=>setShowProfile(true)}
                  style={{ padding:'8px 16px', borderRadius:'var(--r-sm)', background:'transparent', border:'1px solid var(--border)', color:'var(--t-muted)', fontSize:'0.85rem', cursor:'pointer', transition:'all 0.15s' }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=roleColor;e.currentTarget.style.color=roleColor;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--t-muted)';}}>
                  ◉ Profile
                </button>
                <button onClick={handleLogout} style={{ padding:'8px 16px', borderRadius:'var(--r-sm)', background:'transparent', border:'1px solid var(--border)', color:'var(--t-muted)', fontSize:'0.85rem', cursor:'pointer' }}>Sign Out</button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {role==='trainer' ? <TrainerDashboard/> : role==='partner' ? <PartnerDashboard/> : <StudentDashboard/>}

      {showProfile && (
        <ProfileModal user={userData} onClose={()=>setShowProfile(false)} onSaved={refreshUser}/>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
